import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import API from "../../utils/api";

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const date = (value) => value ? new Date(value).toLocaleDateString("en-IN") : "—";

export default function TestDataCleanup() {
  const [query, setQuery] = useState("");
  const [trips, setTrips] = useState([]);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const load = async (q = query) => {
    setLoading(true);
    try {
      const [tripRes, historyRes] = await Promise.all([
        API.get("/admin-cleanup/trips", { params: { q } }),
        API.get("/admin-cleanup/history"),
      ]);
      setTrips(tripRes.data || []);
      setHistory(historyRes.data || []);
    } catch { toast.error("Could not load testing-data cleanup."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(""); }, []);

  const preview = async (id) => {
    try {
      const { data } = await API.get(`/admin-cleanup/trips/${id}/preview`);
      setSelected(data);
      setReason("");
      setConfirmation("");
    } catch { toast.error("Could not inspect this trip."); }
  };

  const remove = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await API.delete(`/admin-cleanup/trips/${selected.trip.id}`, {
        data: { reason: reason.trim(), confirmation: confirmation.trim() },
      });
      toast.success("Testing trip and all related records were removed.");
      setSelected(null);
      await load(query);
    } catch (err) {
      toast.error(err.response?.data?.error || "Cleanup failed.");
    } finally { setDeleting(false); }
  };

  const confirmPhrase = selected ? `DELETE TRIP ${selected.trip.id}` : "";
  const reasonOk = reason.trim().length >= 5;
  const confirmOk = confirmation.trim() === confirmPhrase;
  const canDelete = !deleting && reasonOk && confirmOk;

  const textColor = "#172033";
  const mutedTextColor = "#334155";
  const card = { background: "#fff", border: "1px solid #dbe4ef", borderRadius: 18, padding: 22, color: textColor };
  const input = { color: textColor, background: "#fff", fontWeight: 600 };
  return <div style={{ display: "grid", gap: 20, color: textColor }}>
    <section style={{ ...card, background: "linear-gradient(135deg,#fff7ed,#fff)" }}>
      <div style={{ color: "#c2410c", fontWeight: 800, letterSpacing: 1, fontSize: 12 }}>SUPERADMIN CONTROL</div>
      <h1 style={{ margin: "8px 0" }}>Testing Data Cleanup</h1>
      <p style={{ margin: 0, color: mutedTextColor }}>Preview and permanently remove a test trip together with its expenses, bills, advances, approvals, queries and settlement records. Every cleanup keeps an audit snapshot.</p>
    </section>

    <section style={card}>
      <form onSubmit={(e) => { e.preventDefault(); load(); }} style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Employee, email, trip or destination" style={{ ...input, flex: "1 1 320px", padding: 13, border: "1px solid #cbd5e1", borderRadius: 10 }} />
        <button type="submit" style={{ padding: "12px 22px", border: 0, borderRadius: 10, background: "#0f766e", color: "white", fontWeight: 800 }}>Search</button>
      </form>
      <div style={{ overflowX: "auto", marginTop: 18 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead><tr>{["Employee","Trip","Period","Amount","Status","Dependencies",""].map(h => <th key={h} style={{ color: textColor, textAlign: "left", padding: 10, borderBottom: "2px solid #e2e8f0" }}>{h}</th>)}</tr></thead>
          <tbody>{!loading && trips.map(t => <tr key={t.id}>
            <td style={{ color: textColor, padding: 10, borderBottom: "1px solid #edf2f7" }}><b>{t.employee_name}</b><br/><small style={{ color: mutedTextColor }}>{t.employee_email}</small></td>
            <td style={{ color: textColor, padding: 10, borderBottom: "1px solid #edf2f7" }}><b>#{t.id} {t.title}</b><br/><small style={{ color: mutedTextColor }}>{t.destination}</small></td>
            <td style={{ padding: 10, borderBottom: "1px solid #edf2f7" }}>{date(t.start_date)} – {date(t.end_date)}</td>
            <td style={{ padding: 10, borderBottom: "1px solid #edf2f7" }}>{money(t.total_trip_cost)}</td>
            <td style={{ padding: 10, borderBottom: "1px solid #edf2f7" }}>{t.status}</td>
            <td style={{ padding: 10, borderBottom: "1px solid #edf2f7" }}>{t.expense_count} expenses · {t.approval_count} approvals · {t.query_count} queries</td>
            <td style={{ padding: 10, borderBottom: "1px solid #edf2f7" }}><button onClick={() => preview(t.id)} style={{ padding: "8px 13px", border: "1px solid #dc2626", color: "#b91c1c", background: "white", borderRadius: 8, fontWeight: 800 }}>Review cleanup</button></td>
          </tr>)}</tbody>
        </table>
        {!loading && !trips.length && <p style={{ color: "#64748b" }}>No matching trips.</p>}
      </div>
    </section>

    {selected && <section style={{ ...card, borderColor: "#fca5a5" }}>
      <h2 style={{ color: "#991b1b" }}>Confirm complete cleanup: trip #{selected.trip.id}</h2>
      <p><b>{selected.trip.employee_name}</b> · {selected.trip.title} · {money(selected.trip.total_trip_cost)}</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "14px 0" }}>
        {Object.entries(selected.counts).filter(([, count]) => Number(count) > 0).map(([name, count]) => <span key={name} style={{ background: "#fff1f2", padding: "6px 10px", borderRadius: 20 }}>{name.replaceAll("_", " ")}: <b>{count}</b></span>)}
        <span style={{ background: "#fff1f2", padding: "6px 10px", borderRadius: 20 }}>uploaded files: <b>{selected.uploaded_files}</b></span>
      </div>
      <label style={{ display: "block", fontWeight: 700 }}>Reason</label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Example: September test trip requested by Superadmin" style={{ ...input, width: "100%", padding: 12, boxSizing: "border-box", margin: "6px 0 8px", border: "1px solid #cbd5e1", borderRadius: 8 }} />
      {!reasonOk && reason.length > 0 && <p style={{ margin: "0 0 12px", color: "#b45309", fontSize: 13 }}>Reason needs at least 5 characters.</p>}
      {reasonOk && <div style={{ height: 12 }} />}

      <label style={{ display: "block", fontWeight: 700 }}>
        Confirmation — type exactly <code style={{ background: "#fee2e2", padding: "2px 6px", borderRadius: 4 }}>{confirmPhrase}</code>
      </label>
      <p style={{ margin: "4px 0 8px", color: mutedTextColor, fontSize: 13 }}>
        This is separate from the reason above. The delete button stays off until this matches exactly (all capitals).
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <input
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          placeholder={confirmPhrase}
          autoComplete="off"
          spellCheck={false}
          style={{
            ...input,
            flex: "1 1 280px",
            padding: 12,
            boxSizing: "border-box",
            border: `1px solid ${confirmation && !confirmOk ? "#f87171" : "#cbd5e1"}`,
            borderRadius: 8,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        />
        <button
          type="button"
          onClick={() => setConfirmation(confirmPhrase)}
          disabled={deleting}
          style={{ padding: "11px 14px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#f8fafc", fontWeight: 700, cursor: "pointer" }}
        >
          Fill exact phrase
        </button>
      </div>
      {confirmation && !confirmOk && (
        <p style={{ margin: "0 0 12px", color: "#b91c1c", fontSize: 13 }}>
          Typed text does not match. Required: <b>{confirmPhrase}</b>
        </p>
      )}
      {confirmOk && <p style={{ margin: "0 0 12px", color: "#15803d", fontSize: 13 }}>Confirmation matched.</p>}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => setSelected(null)} disabled={deleting} style={{ padding: "11px 18px", borderRadius: 9, border: "1px solid #cbd5e1", background: "white" }}>Cancel</button>
        <button
          onClick={remove}
          disabled={!canDelete}
          title={!canDelete ? `Enter a reason, then type ${confirmPhrase}` : "Permanently delete this trip"}
          style={{ padding: "11px 18px", borderRadius: 9, border: 0, background: "#b91c1c", color: "white", fontWeight: 800, opacity: canDelete ? 1 : .45, cursor: canDelete ? "pointer" : "not-allowed" }}
        >
          {deleting ? "Removing…" : "Delete trip and related data"}
        </button>
      </div>
    </section>}

    <section style={card}>
      <h2>Cleanup history</h2>
      {!history.length ? <p style={{ color: mutedTextColor }}>No in-app cleanups recorded yet.</p> : history.map(item => <div key={item.id} style={{ color: textColor, padding: "10px 0", borderBottom: "1px solid #e2e8f0" }}><b>Trip #{item.trip_id} · {item.trip_title}</b> — {item.employee_name}<br/><small style={{ color: mutedTextColor }}>{item.reason} · removed by {item.deleted_by_name} · {new Date(item.deleted_at).toLocaleString("en-IN")}</small></div>)}
    </section>
  </div>;
}
